import type { AppConfig } from '@/config/loadConfig';

const STORAGE_KEY = 'aita.debugAuthOverride';

interface DebugAuthOverride {
  msalClientId?: string;
  msalAuthority?: string;
  msalRedirectUri?: string;
}

/**
 * Non-prod escape hatch for testing against a second app registration (e.g. a
 * temporary test app alongside the first-party app) without a pipeline
 * redeploy to flip config.json's msalClientId back and forth.
 *
 * Activate with `?debugClientId=<id>` (optionally `&debugAuthority=...` and/or
 * `&debugRedirectUri=...` if the test app's authority/redirect URI differ from
 * the deployed config). The override persists in sessionStorage — not
 * localStorage, MSAL owns actual auth state; this is purely a dev/test config
 * override — so it survives the MSAL redirect round-trip and stays active for
 * the rest of the tab's session. `?debugClientId=reset` clears it.
 */
export function applyDebugAuthOverride(config: AppConfig): AppConfig {
  if (config.environment === 'production') return config;

  const params = new URLSearchParams(window.location.search);
  const requestedClientId = params.get('debugClientId');

  if (requestedClientId === 'reset') {
    sessionStorage.removeItem(STORAGE_KEY);
    stripDebugParams(params);
    return config;
  }

  if (requestedClientId) {
    const override: DebugAuthOverride = {
      msalClientId: requestedClientId,
      msalAuthority: params.get('debugAuthority') ?? undefined,
      msalRedirectUri: params.get('debugRedirectUri') ?? undefined,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(override));
    stripDebugParams(params);
    return { ...config, ...compact(override) };
  }

  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return config;

  const override = parseStoredOverride(stored);
  if (!override) {
    // Corrupted, tampered, or wrong-shaped value — don't let it silently
    // widen config.msalClientId etc. to a non-string type.
    sessionStorage.removeItem(STORAGE_KEY);
    return config;
  }

  return { ...config, ...compact(override) };
}

/** Parses and validates a stored override, rejecting anything whose present fields aren't strings. */
function parseStoredOverride(stored: string): DebugAuthOverride | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const record = parsed as Record<string, unknown>;
  const fields: (keyof DebugAuthOverride)[] = ['msalClientId', 'msalAuthority', 'msalRedirectUri'];

  const override: DebugAuthOverride = {};
  let hasAnyField = false;
  for (const field of fields) {
    const value = record[field];
    if (value === undefined) continue;
    if (typeof value !== 'string') return null;
    override[field] = value;
    hasAnyField = true;
  }

  return hasAnyField ? override : null;
}

function compact(override: DebugAuthOverride): Partial<AppConfig> {
  const result: Partial<AppConfig> = {};
  if (override.msalClientId) result.msalClientId = override.msalClientId;
  if (override.msalAuthority) result.msalAuthority = override.msalAuthority;
  if (override.msalRedirectUri) result.msalRedirectUri = override.msalRedirectUri;
  return result;
}

/** Removes the debug params from the visible URL so they aren't re-applied via history/bookmarks with stale values. */
function stripDebugParams(params: URLSearchParams): void {
  params.delete('debugClientId');
  params.delete('debugAuthority');
  params.delete('debugRedirectUri');
  const query = params.toString();
  const newUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);
}
